function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

export async function fetchTestProgressList(paperType, attemptId) {
  const params = new URLSearchParams();
  if (paperType) params.set('paperType', paperType);
  if (attemptId) params.set('attemptId', attemptId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`/api/test-progress${qs}`, { headers: authHeaders() });
  if (!res.ok) return [];
  return res.json();
}

export async function putTestProgress(paperSlug, payload) {
  const res = await fetch(`/api/test-progress/${encodeURIComponent(paperSlug)}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to save progress');
  }
  return res.json();
}

export async function deleteAllTestProgress(paperType) {
  const qs = paperType ? `?paperType=${encodeURIComponent(paperType)}` : '';
  const res = await fetch(`/api/test-progress${qs}`, {
    method: 'DELETE',
    headers: authHeaders()
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete test progress');
  }
  return res.json();
}

export async function deleteTestProgress(paperSlug, paperType) {
  const qs = paperType ? `?paperType=${encodeURIComponent(paperType)}` : '';
  const res = await fetch(`/api/test-progress/${encodeURIComponent(paperSlug)}${qs}`, {
    method: 'DELETE',
    headers: authHeaders()
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete progress');
  }
  return res.json();
}

export async function fetchTestSubmissions(paperType, attemptId) {
  const params = new URLSearchParams();
  if (paperType) params.set('paperType', paperType);
  params.set('limit', '50');
  if (attemptId) params.set('attemptId', attemptId);
  const qs = `?${params.toString()}`;
  const res = await fetch(`/api/test-submissions${qs}`, { headers: authHeaders() });
  if (!res.ok) return [];
  const data = await res.json().catch(() => null);
  return Array.isArray(data) ? data : [];
}

export async function postTestSubmission(payload) {
  const res = await fetch('/api/test-submissions', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to save submission');
  }
  return res.json();
}

export async function postInterestTestDraw(body = {}) {
  const res = await fetch('/api/interest-test/draw', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to draw interest test questions');
  }
  return res.json();
}

export async function fetchInterestTriGroupSession() {
  const res = await fetch('/api/interest-test/tri-group/session', {
    headers: authHeaders()
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to load tri-group session');
  }
  return res.json();
}

export async function patchSubmissionClusterSnapshot(submissionId, clusterSnapshot, paperType = 'interest') {
  const res = await fetch(
    `/api/test-submissions/${encodeURIComponent(submissionId)}/cluster-snapshot`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ paperType, clusterSnapshot })
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to save cluster snapshot');
  }
  return res.json();
}

/**
 * Presenter/staff submission feed (server-paginated; not used for analytics totals).
 * @param {{ page?: number, limit?: number, paperSlug?: string, userId?: string, paperType?: string }} params
 * @returns {Promise<{ data: object[], total: number, page: number, limit: number, totalPages: number }>}
 */
export async function fetchAllTestSubmissions(params = {}) {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set('page', String(params.page));
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.paperSlug) qs.set('paperSlug', params.paperSlug);
  if (params.userId) qs.set('userId', params.userId);
  if (params.paperType != null && params.paperType !== '') qs.set('paperType', params.paperType);
  if (params.excludeInternal === '1') qs.set('excludeInternal', '1');
  const res = await fetch(`/api/test-submissions/all?${qs.toString()}`, {
    headers: authHeaders()
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.message || 'Failed to load test submissions');
  }
  // Backward-compatible if an older server still returns a bare array
  if (Array.isArray(data)) {
    return {
      data,
      total: data.length,
      page: 1,
      limit: data.length || params.limit || 20,
      totalPages: 1
    };
  }
  return {
    data: Array.isArray(data?.data) ? data.data : [],
    total: Number(data?.total) || 0,
    page: Math.max(1, Number(data?.page) || 1),
    limit: Math.max(1, Number(data?.limit) || params.limit || 20),
    totalPages: Math.max(1, Number(data?.totalPages) || 1)
  };
}

/**
 * Presenter/staff aggregate analytics over all matching submissions.
 * @param {{ paperSlug?: string, userId?: string, paperType?: string }} params
 */
export async function fetchTestSubmissionAnalytics(params = {}) {
  const qs = new URLSearchParams();
  if (params.paperSlug) qs.set('paperSlug', params.paperSlug);
  if (params.userId) qs.set('userId', params.userId);
  if (params.paperType != null && params.paperType !== '') qs.set('paperType', params.paperType);
  if (params.excludeInternal === '1') qs.set('excludeInternal', '1');
  const res = await fetch(`/api/test-submissions/analytics?${qs.toString()}`, {
    headers: authHeaders()
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.message || 'Failed to load test analytics');
  }
  return data;
}
